import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  QuestionNumberRange as QuestionRange, DigitalSubmissionNumberRange, QuestionTypeV2, RootState,
} from '../../../redux/types';
import useDebounce from '../../../hooks/useDebounce';
import { realNumberRegexTwoDec } from '../../../utilities/regex';
import { formatNumber } from '../../../utilities/numberHelper';
import useQuestionSaveStatus, { QuestionSaveOp } from '../../../hooks/survey/useQuestionSaveStatus';

export type Props = {
  enrollmentId?: number;
  sectionId?: number;
  caseId?: number;
  question: QuestionRange;
  disabled?: boolean;
  defaultSubmission?: DigitalSubmissionNumberRange;
  required?: boolean;
  onSave?: (val: DigitalSubmissionNumberRange | Omit<DigitalSubmissionNumberRange, 'id'> | undefined) => Promise<void>;
  // remember to pass the callbacks wrapped in useCallback
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  manualSave?: boolean;
  saveOnBlur?: boolean;
}

const NumberRangeQuestionInput: React.FC<Props> = ({
  enrollmentId,
  sectionId,
  caseId,
  question,
  disabled,
  defaultSubmission,
  required,
  onSave,
  getSaveStatus,
  manualSave,
  saveOnBlur,
}: Props) => {
  const [saveStatus, setSaveStatus] = useQuestionSaveStatus(question.id, caseId);
  const locale = useSelector((state: RootState) => state.intl.locale);
  const [answer, setAnswer] = useState<string>('');
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (defaultSubmission?.answer === '-' || defaultSubmission?.answer === 'RTU') {
      setAnswer(defaultSubmission?.answer);
    } else {
      setAnswer(formatNumber(locale, defaultSubmission?.answer) || '');
    }
  }, [defaultSubmission, caseId, locale]);

  useEffect(() => {
    setSaveStatus('idle');
    setError(undefined);
  }, [caseId, sectionId, enrollmentId, setSaveStatus]);

  useEffect(() => {
    if (getSaveStatus) {
      getSaveStatus(saveStatus.op, saveStatus.questionId, saveStatus.caseId);
    }
  }, [getSaveStatus, saveStatus]);

  const getAnswerObj = (newAns: number | 'RTU' | '-' | null): DigitalSubmissionNumberRange | Omit<DigitalSubmissionNumberRange, 'id'> | undefined => {
    if (!enrollmentId || !sectionId) {
      return undefined;
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: newAns,
      } as DigitalSubmissionNumberRange;
    }
    const newAnswer: Omit<DigitalSubmissionNumberRange, 'id'> = {
      answer: newAns,
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      type: QuestionTypeV2.NumberRange,
      digitalCaseId: caseId,
    };
    return newAnswer;
  };

  const validateAndSave = (value: string) => {
    if (!onSave) {
      return;
    }
    if (realNumberRegexTwoDec.test(value)) {
      const cleanFormat = value.replace(/,/g, '.');
      // to delay number from auto format for cases like '1.' or '1.0'
      // after an auto save
      // see QZ-915
      const remaining = cleanFormat.split('.')[1];
      const lastChar = cleanFormat.split('').pop();
      const remainingIsZero = !Number.isNaN(remaining) && Number(remaining) === 0;
      if (remainingIsZero || lastChar === '.') {
        setError('INVALID_NUMBER');
        return;
      }
      const answerObj = getAnswerObj(Number(cleanFormat));
      setSaveStatus('saving');
      onSave(answerObj)
        .then(() => {
          setSaveStatus('saved');
          setError(undefined);
        })
        .catch((e) => {
          setError((e as Error).message);
          setSaveStatus('idle');
        });
    } else if (value === '-' || value === 'RTU') {
      setSaveStatus('saving');
      onSave(getAnswerObj(value))
        .then(() => {
          setSaveStatus('saved');
          setError(undefined);
        })
        .catch((e) => {
          setError((e as Error).message);
          setSaveStatus('idle');
        });
    } else if (value === '') {
      setSaveStatus('saving');
      onSave(getAnswerObj(null))
        .then(() => {
          setSaveStatus('saved');
          setError(undefined);
        })
        .catch((e) => {
          setError((e as Error).message);
          setSaveStatus('idle');
        });
    } else {
      setError('INVALID_NUMBER');
    }
  };

  const debounceSaveHandler = (debouncedAnswer: string) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    validateAndSave(debouncedAnswer);
  };

  const saveOnClickHandler = () => {
    if (!manualSave) {
      return;
    }
    validateAndSave(answer);
  };

  const saveOnBlurHandler = () => {
    if (!saveOnBlur) {
      return;
    }
    validateAndSave(answer);
  };

  const saveDebounce = useDebounce<string>(debounceSaveHandler, 1000);

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    const { value } = e.target;
    setAnswer(value);
    setError(undefined);
    if (!manualSave && !saveOnBlur) {
      setSaveStatus('debounceStarting');
      saveDebounce(value);
    }
  };

  return (
    <Box>
      <Typography fontWeight={500} gutterBottom>
        {question.text}
      </Typography>
      <Box>
        <TextField
          size="small"
          id="number-question-input"
          placeholder={question.placeholder || ''}
          value={answer}
          onChange={onTextChange}
          fullWidth
          disabled={disabled}
          required={required}
          error={!!error}
          autoFocus={false}
          autoComplete="off"
          onBlur={saveOnBlurHandler}
          onFocus={() => setSaveStatus('focused')}
        />
      </Box>
      {
        saveStatus.op === 'saved' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography color="primary.main" justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saved" />
            </Typography>
          </Box>
        )
      }
      {
        saveStatus.op === 'saving' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saving" />
            </Typography>
          </Box>
        )
      }
      {
        error && (
          <Typography color="secondary" variant="caption">
            {
              error === 'INVALID_NUMBER'
                ? <FormattedMessage id="numberRangeQuestionInput.invalidNumber" /> : error
            }
          </Typography>
        )
      }
      {
        manualSave && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={saveStatus.op === 'saving'}
              onClick={saveOnClickHandler}
              startIcon={saveStatus.op === 'saving' && (
                <CircularProgress sx={{ color: 'primary.dark' }} size={18} />
              )}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default NumberRangeQuestionInput;
